import { ref, reactive, onMounted } from 'vue'
import axios from 'axios'

export function useSaveCarFilterModal(props) {
  // Configure axios to send CSRF token with every request
  axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
  const token = document.head.querySelector('meta[name="csrf-token"]')
  if (token) {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = token.content
    // Also set the XSRF-TOKEN cookie value as X-XSRF-TOKEN header
    axios.defaults.withCredentials = true
  } else {
    console.error('CSRF token not found: https://laravel.com/docs/csrf#csrf-x-csrf-token')
  }

  // State
  const step = ref(1)
  const savedFilterAction = ref('create')
  const savedFilterName = ref('')
  const savedFilterId = ref('')
  const notificationMode = ref('daily')
  const errors = reactive({})
  const savedFilters = ref([])
  const finished = ref(false)
  const finishedSavedCarFilterName = ref('')
  const isAuthenticated = ref(false)
  const showAuthForm = ref(false)
  const loginEmail = ref('')
  const loginPassword = ref('')
  const registrationEmail = ref('')
  const registrationPassword = ref('')
  const registrationPasswordConfirmation = ref('')
  const activeAuthTab = ref('login') // 'login' or 'register'
  const shouldReloadAfterClose = ref(false)

  // Methods
  function resetModalState() {
    // Check if we need to reload the page after closing
    const needsReload = shouldReloadAfterClose.value

    step.value = 1
    savedFilterAction.value = 'create'
    savedFilterName.value = ''
    savedFilterId.value = ''
    notificationMode.value = 'daily'
    Object.keys(errors).forEach(key => delete errors[key])
    finished.value = false
    finishedSavedCarFilterName.value = ''
    showAuthForm.value = false
    loginEmail.value = ''
    loginPassword.value = ''
    registrationEmail.value = ''
    registrationPassword.value = ''
    registrationPasswordConfirmation.value = ''
    activeAuthTab.value = 'login'
    shouldReloadAfterClose.value = false

    // Reload the page after modal is closed if needed
    if (needsReload) {
      window.location.reload()
    }
  }

  function nextStep() {
    // Validate current step
    if (step.value === 1 && savedFilterAction.value === 'update' && !savedFilterId.value) {
      errors.savedFilterId = 'Vyberte filtr, který chcete aktualizovat'
      return
    }

    // Validate filter name - step 2 for authenticated users with existing filters,
    // or step 1 when there are no saved filters (step 1 then shows the create form directly)
    if ((step.value === 2 || (step.value === 1 && savedFilters.value.length === 0)) && !savedFilterName.value && savedFilterAction.value === 'create') {
      errors.savedFilterName = 'Zadejte název filtru'
      return
    }

    // Clear errors
    Object.keys(errors).forEach(key => delete errors[key])

    // If we're on step 1 and action is update, go to step 3
    if (step.value === 1 && savedFilterAction.value === 'update') {
      step.value = 3
      return
    }

    // Special case: If we're on step 1 with no saved filters (unauthenticated users),
    // and we're creating a new filter, skip step 2 and go directly to step 3
    if (step.value === 1 && savedFilters.value.length === 0 && savedFilterAction.value === 'create') {
      step.value = 3
      return
    }

    // Otherwise, increment step
    step.value++
  }

  async function submit() {
    try {
      // Check if user is authenticated
      if (!isAuthenticated.value) {
        // Check authentication status again to be sure
        const authenticated = await checkAuthStatus()
        if (!authenticated) {
          // Show authentication form
          showAuthForm.value = true
          return
        }
      }

      // Clear errors
      Object.keys(errors).forEach(key => delete errors[key])

      // Prepare data
      const data = {
        savedFilterAction: savedFilterAction.value,
        notificationMode: notificationMode.value,
        currentFilterSettings: props.currentFilterSettings,
        currentFilterResults: props.currentFilterResults
      }

      if (savedFilterAction.value === 'create') {
        data.savedFilterName = savedFilterName.value
      } else {
        data.savedFilterId = savedFilterId.value
        // Include the name of the selected filter when updating
        const selectedFilter = savedFilters.value.find(filter => filter.id === savedFilterId.value)
        if (selectedFilter) {
          data.savedFilterName = selectedFilter.name
        }
      }

      // Submit data
      const response = await axios.post('/api/v1/saved-car-filters', data)

      // Handle success
      finishedSavedCarFilterName.value = response.data.name
      finished.value = true

      // Dispatch event for analytics
      window.dispatchEvent(new CustomEvent('filterSaved', {
        detail: {
          payload: {
            event: 'filter_cars_save',
            filter: {
              id: response.data.id,
              notification_frequency: notificationMode.value,
              name: response.data.name,
              settings: props.currentFilterSettings,
              result_ids: props.currentFilterResults
            },
            user: response.data.user
          }
        }
      }))
    } catch (error) {
      // Handle unauthorized error
      if (error.response && error.response.status === 401) {
        // Show authentication form
        showAuthForm.value = true
      }
      // Handle validation errors
      else if (error.response && error.response.status === 422) {
        const validationErrors = error.response.data.errors
        Object.keys(validationErrors).forEach(key => {
          errors[key] = validationErrors[key][0]
        })
      } else {
        // Handle other errors
        console.error('Error saving filter:', error)
        errors.general = 'Došlo k chybě při ukládání filtru. Zkuste to prosím znovu.'
      }
    }
  }

  // Check if user is authenticated
  async function checkAuthStatus() {
    try {
      const response = await axios.get('/api/v1/auth/check')
      isAuthenticated.value = response.data.authenticated
      return isAuthenticated.value
    } catch (error) {
      console.error('Error checking authentication status:', error)
      isAuthenticated.value = false
      return false
    }
  }

  // Handle login
  async function login() {
    try {
      // Clear errors
      Object.keys(errors).forEach(key => delete errors[key])

      // Validate
      if (!loginEmail.value) {
        errors.loginEmail = 'Zadejte e-mail'
        return
      }
      if (!loginPassword.value) {
        errors.loginPassword = 'Zadejte heslo'
        return
      }

      // Submit login
      const response = await axios.post('/login', {
        email: loginEmail.value,
        password: loginPassword.value
      })

      // If successful, update auth status and continue with filter saving
      await checkAuthStatus()
      if (isAuthenticated.value) {
        showAuthForm.value = false
        // Set flag to reload after modal is closed
        shouldReloadAfterClose.value = true
        // Continue with filter saving
        await submit()
      }
    } catch (error) {
      console.error('Login error:', error)
      errors.loginEmail = 'Nesprávné přihlašovací údaje'
    }
  }

  // Handle registration
  async function register() {
    try {
      // Clear errors
      Object.keys(errors).forEach(key => delete errors[key])

      // Validate
      if (!registrationEmail.value) {
        errors.registrationEmail = 'Zadejte e-mail'
        return
      }
      if (!registrationPassword.value) {
        errors.registrationPassword = 'Zadejte heslo'
        return
      }
      if (registrationPassword.value !== registrationPasswordConfirmation.value) {
        errors.registrationPasswordConfirmation = 'Hesla se neshodují'
        return
      }

      // Submit registration
      const response = await axios.post('/register', {
        email: registrationEmail.value,
        password: registrationPassword.value,
        password_confirmation: registrationPasswordConfirmation.value,
        name: registrationEmail.value.split('@')[0] // Use part of email as name
      })

      // If successful, update auth status and continue with filter saving
      await checkAuthStatus()
      if (isAuthenticated.value) {
        showAuthForm.value = false
        // Set flag to reload after modal is closed
        shouldReloadAfterClose.value = true
        // Continue with filter saving
        await submit()
      }
    } catch (error) {
      console.error('Registration error:', error)
      if (error.response && error.response.data && error.response.data.errors) {
        const validationErrors = error.response.data.errors
        Object.keys(validationErrors).forEach(key => {
          errors[key === 'email' ? 'registrationEmail' : key] = validationErrors[key][0]
        })
      } else {
        errors.registrationEmail = 'Chyba při registraci'
      }
    }
  }

  // Load saved filters on mount
  onMounted(async () => {
    // Check authentication status first
    await checkAuthStatus()

    try {
      if (isAuthenticated.value) {
        const response = await axios.get('/api/v1/saved-car-filters')
        savedFilters.value = response.data

        // If there are no saved filters, start at step 2 (create new)
        if (savedFilters.value.length === 0) {
          step.value = 2
        }
      }
    } catch (error) {
      console.error('Error loading saved filters:', error)
    }

    // Add event listener for modal hidden event
    // This ensures resetModalState is called when the modal is closed by any means
    // (clicking X, clicking outside, pressing ESC)
    const modalElement = document.getElementById('savedFilterModal')
    if (modalElement) {
      modalElement.addEventListener('hidden.bs.modal', resetModalState)
    }
  })

  return {
    // State
    step,
    savedFilterAction,
    savedFilterName,
    savedFilterId,
    notificationMode,
    errors,
    savedFilters,
    finished,
    finishedSavedCarFilterName,
    isAuthenticated,
    showAuthForm,
    loginEmail,
    loginPassword,
    registrationEmail,
    registrationPassword,
    registrationPasswordConfirmation,
    activeAuthTab,
    shouldReloadAfterClose,

    // Methods
    resetModalState,
    nextStep,
    submit,
    checkAuthStatus,
    login,
    register
  }
}
